import { ArrowRight, ArrowUpRight, Camera, MessageSquareHeart, PenLine, Sparkles } from "lucide-react";
import { OCCASIONS } from "@/data/siteData";
import { ChapterHeading, Em, Reveal } from "./primitives";

const CUSTOM_OPTIONS = [
  { icon: PenLine, label: "Names & Initials" },
  { icon: Camera, label: "Photos" },
  { icon: MessageSquareHeart, label: "Messages" },
  { icon: Sparkles, label: "Special Memories" },
];

export default function Occasions({ content, onNavigate }) {
  const c = content || {};
  const items = c.items?.length ? c.items : OCCASIONS.map((o) => o.name);

  return (
    <section
      id="occasions"
      className="bg-ink py-24 text-cream md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterHeading
          dark
          number={(c.kicker || "04 — Occasions").split("—")[0].trim()}
          kicker={(c.kicker || "04 — Occasions").split("—")[1]?.trim() || "Occasions"}
          title={<Em text={c.title || "Gifts For Every *Occasion*"} />}
          blurb={c.blurb || "From the biggest milestones to the smallest just-because Tuesdays — there is always a reason to gift."}
        />

        <div className="grid gap-x-16 md:grid-cols-2">
          {items.map((name, i) => (
            <Reveal key={name} delay={0.04 * i}>
              <button
                data-testid={`occasion-row-${name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => onNavigate("#contact")}
                className="group flex w-full items-center justify-between border-b border-white/10 py-6 text-left"
              >
                <div className="flex items-baseline gap-5">
                  <span className="font-serif text-sm italic text-rose/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-2xl font-medium transition-colors duration-300 group-hover:text-rose md:text-4xl">
                    {name}
                  </span>
                </div>
                <ArrowUpRight
                  size={22}
                  className="text-white/30 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-rose"
                />
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-24">
          <div className="grid items-center gap-10 rounded-[32px] border border-white/10 bg-white/5 p-8 md:p-14 lg:grid-cols-2">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-rose">
                {c.cta_kicker || "Personalized Gifts"}
              </p>
              <h3 className="font-serif text-3xl font-medium leading-tight md:text-5xl">
                <Em text={c.cta_heading || "Your Story. Your Gift. *Your Way.*"} emClass="italic text-rose" />
              </h3>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60 md:text-base">
                {c.cta_text || "Add names, photographs, heartfelt messages and the little memories only you two understand. We will craft a gift that could belong to no one else."}
              </p>
            </div>
            <div className="flex flex-col gap-8">
              <div className="flex flex-wrap gap-3">
                {CUSTOM_OPTIONS.map((opt) => (
                  <span
                    key={opt.label}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-xs font-medium text-white/80"
                  >
                    <opt.icon size={14} className="text-rose" />
                    {opt.label}
                  </span>
                ))}
              </div>
              <button
                data-testid="create-your-gift-button"
                onClick={() => onNavigate("#contact")}
                className="group inline-flex w-fit items-center gap-2 rounded-full bg-rose px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-rose-dark"
              >
                {c.cta_button || "Create Your Gift"}
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
